import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNaN, isNil } from 'lodash-es';
import { memo } from 'react';

import { Order } from '~hooks/useOrders/models';
import { dateFormat } from '~utils/dateTime';

interface OrderInfoProps {
  order: Order;
  sx?: SxProps;
}

const OrderInfo = memo(function OrderInfo({ order, sx }: OrderInfoProps) {
  const quantity = order.quantity;
  const uom = order.unitOfMeasure?.name;
  const material = order.material?.name;
  const quantityString = isNil(quantity) || isNaN(uom) ? null : `${quantity} ${uom}`;
  const fragments = [
    dateFormat(dayjs(order.loadAt), 'hh:mmA · MMM DD'),
    quantityString,
    material,
  ].filter(Boolean);
  const infoString = fragments.join(' • ');

  return (
    <Box
      sx={[
        {
          width: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {infoString && (
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {infoString}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: 0,
          mt: 1,
        }}
      >
        {order.company?.legalName && (
          <OrderAttribute
            name={`${t('live_map.assigning_company')}`}
            text={order.company.legalName}
          />
        )}

        <OrderAttribute
          name={`${t('common.customer')}`}
          text={order.account?.name ?? ''}
        />

        <OrderAttribute
          name={`${t('live_map.pickup_site')}`}
          text={order.pickUpWayPoints?.[0]?.site?.name ?? ''}
        />

        <OrderAttribute
          name={`${t('live_map.dropoff_site')}`}
          text={order.dropOffWayPoints?.[0]?.site?.name ?? ''}
        />
      </Box>
    </Box>
  );
});

interface OrderAttributeProps {
  name?: string;
  text: string;
}

const OrderAttribute = memo(function OrderAttribute({ name, text }: OrderAttributeProps) {
  const theme = useTheme();

  return (
    <Typography
      sx={{
        fontSize: '12px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {name && (
        <Typography
          component="span"
          color={theme.palette.text.secondary}
          sx={{
            fontSize: 'inherit',
          }}
        >
          {name}:
        </Typography>
      )}{' '}
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
        }}
      >
        {text}
      </Typography>
    </Typography>
  );
});

export default OrderInfo;
